import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  PhoneMissed,
  MessageSquareText,
  CalendarCheck2,
  ArrowRight,
  Gauge,
} from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { Eyebrow } from "./Reveal";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const renderLine = (line) => {
  if (!line.accent) return line.text;
  const [pre, post] = line.text.split(line.accent);
  return (
    <>
      {pre}
      <span className="text-[#F25F22]">{line.accent}</span>
      {post}
    </>
  );
};

const DEFAULT_CONTENT = {
  eyebrow: "Revenue Recovery for HVAC Contractors",
  lines: [
    { text: "Every missed call" },
    { text: "is a $4,500 job", accent: "$4,500 job" },
    { text: "for somebody else." },
  ],
  subtext:
    "Backfill answers in seconds — a company-branded text, smart qualifying questions, and a human dispatcher when it matters. Missed calls, slow web leads, and forgotten estimates become booked service jobs. No new hire required.",
  integrationLine: "Works with your existing phone line — no CRM required",
  image:
    "https://images.unsplash.com/photo-1660330589827-da8ab7dd3c02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxIVkFDJTIwdGVjaG5pY2lhbiUyMHdvcmtpbmclMjBoZWF0aW5nJTIwYWlyJTIwY29uZGl0aW9uaW5nfGVufDB8fHx8MTc5MTEyNTExNnww&ixlib=rb-4.1.0&q=85",
  imageAlt: "HVAC technician servicing a unit",
  qualifiedLabel: "Qualified: emergency AC repair · booked 2–4 PM",
};

export default function Hero({ content = DEFAULT_CONTENT }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="noise relative overflow-hidden pt-20 pb-24 sm:pt-24 lg:pb-32"
    >
      <div className="bg-grid absolute inset-0 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]" />
      <div className="absolute -top-40 right-[-10%] h-[520px] w-[520px] rounded-full bg-[#F25F22]/[0.12] blur-[140px]" />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <Eyebrow>{content.eyebrow}</Eyebrow>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="mt-5 font-display text-4xl font-bold leading-[1.06] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl"
          >
            {content.lines.map((line, i) => (
              <Line key={line.text} delay={0.25 + i * 0.13}>
                {renderLine(line)}
              </Line>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
          >
            {content.subtext}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="/signup"
              data-testid="hero-primary-cta"
              className="group flex items-center gap-2 rounded-md bg-[#F25F22] px-6 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.02] hover:bg-[#FF7539] hover:shadow-[0_0_32px_rgba(242,95,34,0.5)]"
            >
              Start Recovering Revenue
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <a
              href="#problem"
              data-testid="hero-secondary-cta"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#problem");
              }}
              className="group flex items-center gap-2 rounded-md border border-white/[0.12] bg-white/[0.03] px-6 py-3.5 text-sm font-semibold text-slate-200 transition-all duration-200 hover:border-[#F25F22]/50 hover:bg-white/[0.06]"
            >
              <Gauge className="h-4 w-4 text-[#F25F22]" />
              Calculate Your Leak
            </a>
              <span className="text-sm text-slate-600">
                or{" "}
                <a href="/demo" data-testid="hero-demo-link" className="font-medium text-slate-500 transition-colors hover:text-[#F25F22]">
                  watch it book a job
                </a>{" "}
                ·{" "}
                <a href="/playground" data-testid="hero-playground-link" className="font-medium text-slate-500 transition-colors hover:text-[#F25F22]">
                  drive it yourself
                </a>
              </span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-8 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500"
            data-testid="hero-integrations-line"
          >
            {content.integrationLine}
          </motion.p>
        </motion.div>

        <motion.div
          style={{ y: imgY }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative lg:col-span-5"
        >
          <div className="relative overflow-hidden rounded-xl border border-white/[0.08] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)]">
            <img
              src={content.image}
              alt={content.imageAlt}
              data-testid="hero-image"
              className="h-[420px] w-full object-cover contrast-[1.05] lg:h-[500px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
            <span className="absolute left-0 top-0 h-5 w-5 border-l-2 border-t-2 border-[#F25F22]" />
            <span className="absolute right-0 top-0 h-5 w-5 border-r-2 border-t-2 border-[#F25F22]" />
            <span className="absolute bottom-0 left-0 h-5 w-5 border-b-2 border-l-2 border-[#F25F22]" />
            <span className="absolute bottom-0 right-0 h-5 w-5 border-b-2 border-r-2 border-[#F25F22]" />
          </div>

          <motion.div
            data-testid="dispatch-node-card"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="absolute -left-4 bottom-8 w-[280px] rounded-lg border border-white/[0.1] bg-[#12161F]/90 p-4 shadow-2xl backdrop-blur-xl sm:-left-8"
          >
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-[#F25F22]" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#F25F22]" />
              </span>
              <PhoneMissed className="h-3.5 w-3.5 text-[#F25F22]" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                Missed call · (555) 014-2231
              </span>
            </div>
            <div className="mt-3 space-y-2 border-t border-white/[0.06] pt-3">
              <div className="flex items-center gap-2">
                <MessageSquareText className="h-3.5 w-3.5 text-emerald-400" />
                <p className="text-xs text-slate-300">
                  Branded SMS sent in{" "}
                  <span className="font-semibold text-emerald-400">8s</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <CalendarCheck2 className="h-3.5 w-3.5 text-emerald-400" />
                <p className="text-xs text-slate-300">{content.qualifiedLabel}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
